import type { UserRole } from '@fig/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

import { myAdvisorsKey } from './useMyAdvisors';

export interface AdminRoleAssignment {
  email: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
  hasTeamAssignment: boolean;
  salesTeamNames: string[];
}

export interface AdminRolesResponse {
  roles: AdminRoleAssignment[];
  summary: {
    adminCount: number;
    vpCount: number;
    totalCount: number;
  };
}

export interface UpdateAdminRoleAssignment {
  email: string;
  role: UserRole;
}

export const adminRolesKey = ['admin', 'roles'] as const;

async function fetchAdminRoles(): Promise<AdminRolesResponse> {
  const response = await apiClient.get<AdminRolesResponse>('/admin/roles');
  return response.data;
}

async function saveAdminRoles(roles: UpdateAdminRoleAssignment[]): Promise<AdminRolesResponse> {
  const response = await apiClient.put<AdminRolesResponse>('/admin/roles', { roles });
  return response.data;
}

export function useAdminRoles() {
  return useQuery({
    queryKey: adminRolesKey,
    queryFn: fetchAdminRoles,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSaveAdminRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveAdminRoles,
    onSuccess: (data) => {
      queryClient.setQueryData(adminRolesKey, data);
      queryClient.invalidateQueries({ queryKey: myAdvisorsKey });
    },
  });
}
